import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Play, ChevronRight, ChevronLeft, Loader2, ListPlus, ArrowRight, Clock, Film } from 'lucide-react';
import PublicLayout from '../../components/public/PublicLayout';
import AlbumCard from '../../components/public/AlbumCard';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';
import SEO from '../../components/SEO';
import { PUBLIC_BASE } from './config';
import useScrollReveal from '../../hooks/useScrollReveal';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

const CATEGORY_COLORS = {
  '마케팅 전략': 'bg-blue-500/15 text-blue-400',
  '유통 가이드': 'bg-orange-500/15 text-orange-400',
  '제작 노하우': 'bg-emerald-500/15 text-emerald-400',
  '트렌드 분석': 'bg-amber-500/15 text-amber-400',
  '수익화 전략': 'bg-purple-500/15 text-purple-400',
  '아티스트 성장': 'bg-cyan-500/15 text-cyan-400',
};

function getMondayDate() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split('T')[0];
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function PlaylistMiniCard({ playlist, isNew }) {
  const navigate = useNavigate();
  const { playImmediate, addBulkToPlaylist } = useMusicPlayer();
  const tracks = (playlist.songs || [])
    .filter((s) => s.fileUrl || s.youtubeVideoId)
    .map((s) => ({
      id: s.id,
      nameKr: s.nameKr,
      artistKr: s.artistKr || '',
      fileUrl: s.fileUrl || null,
      youtubeVideoId: s.youtubeVideoId || null,
      albumImage: s.albumImage || FALLBACK_IMG,
      albumName: s.albumName || '',
      playlistSongId: s.playlistSongId || null,
    }));

  const goDetail = () => navigate(`${PUBLIC_BASE}/playlists/${playlist.id}`);
  const handlePlayAll = (e) => { e.stopPropagation(); if (tracks.length > 0) playImmediate(tracks); };
  const handleAddAll = (e) => { e.stopPropagation(); if (tracks.length > 0) addBulkToPlaylist(tracks); };

  return (
    <div
      onClick={goDetail}
      className="group/card bg-[#182334] rounded-2xl overflow-hidden border border-white/5 cursor-pointer hover:border-white/15 transition-all hover:shadow-xl hover:shadow-black/20">
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        <img
          src={playlist.coverImage || FALLBACK_IMG}
          alt={playlist.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <button
          onClick={handlePlayAll}
          disabled={tracks.length === 0}
          className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition-colors group">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 ${tracks.length > 0 ? 'bg-sky-500' : 'bg-slate-700'}`}>
            <Play size={20} fill="currentColor" className="text-white ml-1" />
          </div>
        </button>
        {isNew && (
          <span className="absolute top-3 right-3 bg-sky-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide shadow-lg">
            NEW
          </span>
        )}
      </div>
      <div className="p-4">
        <span className="inline-block text-xs text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded-full mb-2">
          {playlist.concept}
        </span>
        <h3 className="text-white font-bold text-sm mb-1 truncate group-hover/card:text-sky-300 transition-colors">{playlist.name}</h3>
        <p className="text-slate-400 text-xs leading-relaxed mb-1.5 line-clamp-2">{playlist.description}</p>
        {(() => {
          const artists = [...new Set((playlist.songs || []).map((s) => s.artistKr).filter(Boolean))];
          const display = artists.slice(0, 3);
          return display.length > 0 ? (
            <p className="text-sky-400/70 text-xs mb-3 truncate">
              {display.join(', ')}{artists.length > 3 ? ' 외' : ''}
            </p>
          ) : null;
        })()}
        <div className="flex items-center gap-1.5">
          <button
            onClick={handlePlayAll}
            disabled={tracks.length === 0}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
              tracks.length > 0 ? 'bg-sky-500 hover:bg-sky-400 text-white' : 'bg-slate-700 text-slate-500 cursor-not-allowed'
            }`}>
            <Play size={11} fill="currentColor" />
            재생
          </button>
          <button
            onClick={handleAddAll}
            disabled={tracks.length === 0}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap ${
              tracks.length > 0 ? 'bg-white/5 hover:bg-white/10 text-slate-300' : 'bg-slate-800 text-slate-600 cursor-not-allowed'
            }`}>
            <ListPlus size={11} />
            담기
          </button>
        </div>
      </div>
    </div>
  );
}

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now - created;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return '오늘';
  if (diffDays === 1) return '어제';
  if (diffDays < 7) return `${diffDays}일 전`;
  const m = created.getMonth() + 1;
  const d = created.getDate();
  return `${m}/${d}`;
}

function MusicVideoSlider({ videos }) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (videos.length <= 1) return;
    const timer = setInterval(() => setIndex(i => (i + 1) % videos.length), 4000);
    return () => clearInterval(timer);
  }, [videos.length]);

  if (!videos.length) return null;
  const mv = videos[index];

  return (
    <div className="hidden lg:flex flex-col w-[320px] xl:w-[360px] shrink-0">
      <div className="flex items-center gap-2 mb-3">
        <Film size={14} className="text-rose-400" />
        <span className="text-xs font-semibold text-rose-400 tracking-wide uppercase">Music Video</span>
      </div>
      <div
        onClick={() => navigate(`${PUBLIC_BASE}/music-videos/${mv.id}`)}
        className="group relative bg-[#182334] rounded-2xl overflow-hidden border border-white/5 cursor-pointer hover:border-white/15 transition-all flex-1 flex flex-col">
        {/* 썸네일 */}
        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
          <img
            key={mv.id}
            src={mv.thumbnail || FALLBACK_IMG}
            alt={mv.title}
            className="absolute inset-0 w-full h-full object-cover animate-fadeIn"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-rose-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
              <Play size={20} fill="currentColor" className="text-white ml-0.5" />
            </div>
          </div>
        </div>
        {/* 정보 */}
        <div className="p-4 flex-1 flex flex-col">
          <h4 className="text-white text-sm font-bold leading-snug truncate group-hover:text-rose-300 transition-colors mb-1.5">
            {mv.title}
          </h4>
          <p className="text-slate-400 text-xs mb-3">{mv.artist}</p>
          <div className="mt-auto flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {videos.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                  className={`h-1 rounded-full transition-all duration-300 ${i === index ? 'w-4 bg-rose-400' : 'w-1.5 bg-white/20 hover:bg-white/40'}`}
                />
              ))}
            </div>
            <span className="text-slate-500 text-[11px]">{index + 1} / {videos.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrendMiniCard({ trend, isNew, hideOnPc }) {
  const navigate = useNavigate();
  const colorClass = CATEGORY_COLORS[trend.category] || 'bg-sky-500/15 text-sky-400';

  return (
    <div
      onClick={() => navigate(`${PUBLIC_BASE}/magazine/music-trends/${trend.id}`)}
      className={`group bg-[#182334] rounded-2xl overflow-hidden border border-white/5 cursor-pointer hover:border-white/15 transition-all hover:shadow-xl hover:shadow-black/20 sm:flex sm:flex-row ${hideOnPc ? 'sm:hidden' : ''}`}>
      {/* 이미지: 모바일 세로형 / PC 가로형 좌측 */}
      <div className="relative w-full aspect-video sm:aspect-auto sm:w-44 md:w-52 shrink-0">
        <img
          src={trend.coverImage || FALLBACK_IMG}
          alt={trend.title}
          className="absolute inset-0 w-full h-full object-cover sm:static sm:w-full sm:h-full sm:rounded-l-2xl sm:rounded-r-none"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors sm:hidden" />
        {isNew && (
          <span className="absolute top-2 right-2 bg-sky-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide shadow-lg">
            NEW
          </span>
        )}
      </div>
      {/* 텍스트: 모바일 하단 / PC 우측 */}
      <div className="p-3 sm:p-4 sm:flex sm:flex-col sm:justify-center sm:flex-1 sm:min-w-0">
        <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full mb-1.5 w-fit ${colorClass}`}>
          {trend.category}
        </span>
        <h3 className="text-white font-bold text-sm mb-1 leading-snug line-clamp-2 group-hover:text-sky-300 transition-colors">
          {trend.title}
        </h3>
        <p className="text-slate-400 text-xs leading-relaxed mb-2 line-clamp-2">{trend.summary}</p>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 text-slate-600 text-[11px]">
            <Clock size={11} />
            {getTimeAgo(trend.createdAt)}
          </span>
          <span className="flex items-center gap-1 text-slate-500 group-hover:text-sky-400 text-xs transition-colors">
            자세히 보기
            <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </div>
  );
}

function PublicHomePage() {
  const navigate = useNavigate();
  const { addBulkToPlaylist, playImmediate } = useMusicPlayer();
  const currentMonday = getMondayDate();
  useScrollReveal(null);

  const [albums, setAlbums] = useState([]);
  const [featuredPlaylists, setFeaturedPlaylists] = useState([]);
  const [musicTrends, setMusicTrends] = useState([]);
  const [heroMusicVideos, setHeroMusicVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroIndex, setHeroIndex] = useState(0);

  const [heroAlbums, setHeroAlbums] = useState([]);
  const [heroTracks, setHeroTracks] = useState(null);

  // 터치 스와이프
  const touchRef = useRef({ startX: 0, startY: 0 });
  const handleTouchStart = (e) => {
    touchRef.current.startX = e.touches[0].clientX;
    touchRef.current.startY = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    const dy = e.changedTouches[0].clientY - touchRef.current.startY;
    if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) setHeroIndex((i) => Math.min(heroAlbums.length - 1, i + 1));
    else setHeroIndex((i) => Math.max(0, i - 1));
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [albumRes, topRes, playlistRes, trendRes, mvRes] = await Promise.all([
          axios.get('/public/albums', { params: { page: 1, limit: 12 } }),
          axios.get('/public/top-albums').catch(() => ({ data: { items: [] } })),
          axios.get('/public/playlists').catch(() => ({ data: [] })),
          axios.get('/public/music-trends').catch(() => ({ data: [] })),
          axios.get('/public/music-videos', { params: { limit: 20, source: 'internal' } }).catch(() => ({ data: { items: [] } })),
        ]);
        setAlbums(albumRes.data.items || []);

        const topItems = topRes.data.items || [];
        setHeroAlbums(shuffleArray(
          topItems.length > 0
            ? topItems.slice(0, 10)
            : (albumRes.data.items || []).slice(0, 5)
        ));

        setFeaturedPlaylists((playlistRes.data?.items || playlistRes.data || []).slice(0, 3));
        setMusicTrends((trendRes.data?.items || trendRes.data || []).slice(0, 3));
        const allMvs = mvRes.data?.items || [];
        const seenArtists = new Set();
        const uniqueMvs = allMvs.filter(mv => {
          if (seenArtists.has(mv.artist)) return false;
          seenArtists.add(mv.artist);
          return true;
        });
        setHeroMusicVideos(uniqueMvs.slice(0, 5));
      } catch (err) {
        console.error('홈 데이터 로드 실패:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // 히어로 자동 롤링 (5초마다)
  useEffect(() => {
    if (heroAlbums.length <= 1) return;
    const timer = setInterval(() => {
      setHeroIndex((i) => (i + 1) % heroAlbums.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroAlbums.length]);

  const hero = heroAlbums[heroIndex] || null;

  const buildTrack = useCallback(
    (song, targetHero) => ({
      id: song.id,
      nameKr: song.nameKr,
      artistKr: song.artistKr || targetHero?.artist?.nameKr || '',
      fileUrl: song.fileUrl,
      albumImage: targetHero?.image || FALLBACK_IMG,
      albumName: targetHero?.nameKr,
    }),
    []
  );

  useEffect(() => {
    if (!hero) { setHeroTracks([]); return; }
    setHeroTracks(null);
    let cancelled = false;
    axios.get(`/public/albums/${hero.id}`)
      .then((res) => {
        if (cancelled) return;
        const songs = (res.data.songs || [])
          .filter((s) => s.fileUrl)
          .map((s) => buildTrack(s, hero));
        setHeroTracks(songs);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('히어로 곡 목록 로드 실패:', err);
          setHeroTracks([]);
        }
      });
    return () => { cancelled = true; };
  }, [hero?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHeroPlay = () => {
    if (heroTracks?.length > 0) playImmediate(heroTracks);
  };

  const handleHeroAddToPlaylist = () => {
    if (!heroTracks?.length) return;
    addBulkToPlaylist(heroTracks);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center h-[60vh] gap-3 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
          <span>로딩 중...</span>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEO
        title="p!nk(핑크) - 음원유통사 | 음원유통 · 정산 플랫폼"
        description="음원유통사 핑크(p!nk). 음원유통, 정산, 아티스트 매니지먼트. 최신음악, 플레이리스트, 뮤직비디오, 뮤직트렌드를 만나보세요."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'p!nk(핑크) - 음원유통사',
          alternateName: ['핑크', 'p!nk', '핑크 유통사', '음원유통사 핑크', 'impink'],
          url: 'https://impink.co.kr',
          description: '음원유통사 핑크(p!nk). 음원유통, 정산, 아티스트 매니지먼트 플랫폼.',
          publisher: { '@type': 'Organization', name: 'p!nk(핑크)', url: 'https://impink.co.kr' },
        }}
      />
      {/* ── 추천 앨범 캐러셀 + 뮤직비디오 ── */}
      {heroAlbums.length > 0 && hero && (
        <section className="border-b border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex gap-5">
          {/* 좌측: 추천 앨범 캐러셀 */}
          <div
            className="relative overflow-hidden rounded-2xl flex-1 min-w-0"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}>
            {/* 블러 배경 */}
            <div
              className="absolute inset-0 bg-cover bg-center scale-110 transition-all duration-700"
              style={{ backgroundImage: `url(${hero.image || FALLBACK_IMG})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d1526] via-[#0d1526]/75 to-[#0d1526]/40" />

            {/* 좌우 화살표 (PC만 표시) */}
            <button
              onClick={() => setHeroIndex((i) => Math.max(0, i - 1))}
              disabled={heroIndex === 0}
              className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 items-center justify-center disabled:opacity-0 transition-all">
              <ChevronLeft size={20} className="text-white" />
            </button>
            <button
              onClick={() => setHeroIndex((i) => Math.min(heroAlbums.length - 1, i + 1))}
              disabled={heroIndex === heroAlbums.length - 1}
              className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 items-center justify-center disabled:opacity-0 transition-all">
              <ChevronRight size={20} className="text-white" />
            </button>

            {/* 컨텐츠 */}
            <div className="relative px-5 sm:px-12 py-10 sm:py-16 flex items-end gap-8">
              <img
                src={hero.image || FALLBACK_IMG}
                alt={hero.nameKr}
                className="hidden sm:block w-32 h-32 md:w-40 md:h-40 rounded-2xl object-cover shadow-2xl shrink-0"
              />
              <div className="min-w-0">
                <p className="text-sky-400 text-xs font-medium tracking-widest uppercase mb-2">
                  추천 앨범
                </p>
                <h2 className="text-white text-2xl md:text-3xl font-bold truncate mb-1">
                  {hero.nameKr}
                </h2>
                <p
                  onClick={() => hero.artist?.id && navigate(`${PUBLIC_BASE}/artists/${hero.artist.id}`)}
                  className={`text-base mb-5 transition-colors ${hero.artist?.id ? 'text-slate-300 cursor-pointer hover:text-sky-400' : 'text-slate-300'}`}>
                  {hero.artist?.nameKr}
                </p>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <button
                    onClick={handleHeroPlay}
                    disabled={!heroTracks?.length}
                    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full font-semibold text-xs sm:text-sm transition-colors shadow-lg ${
                      heroTracks?.length
                        ? 'bg-sky-500 hover:bg-sky-400 text-white'
                        : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                    }`}>
                    <Play size={13} fill="currentColor" className="sm:w-[15px] sm:h-[15px]" />
                    재생
                  </button>
                  <button
                    onClick={handleHeroAddToPlaylist}
                    disabled={!heroTracks?.length}
                    className={`flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm transition-colors ${
                      heroTracks?.length
                        ? 'bg-white/10 hover:bg-white/20 text-white'
                        : 'bg-slate-800 text-slate-600 cursor-not-allowed'
                    }`}>
                    <ListPlus size={13} className="sm:w-[15px] sm:h-[15px]" />
                    담기
                  </button>
                  <button
                    onClick={() => navigate(`${PUBLIC_BASE}/albums/${hero.id}`)}
                    className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 bg-white/10 hover:bg-white/20 rounded-full text-white text-xs sm:text-sm transition-colors">
                    앨범보기
                  </button>
                </div>
              </div>
            </div>

            {/* 점 인디케이터 + 모바일 좌우 버튼 */}
            <div className="relative flex items-center justify-center gap-2 pb-4">
              <button
                onClick={() => setHeroIndex((i) => Math.max(0, i - 1))}
                disabled={heroIndex === 0}
                className="sm:hidden w-7 h-7 rounded-full bg-white/10 flex items-center justify-center disabled:opacity-30 transition-all shrink-0">
                <ChevronLeft size={16} className="text-white" />
              </button>
              <div className="flex items-center gap-2">
                {heroAlbums.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setHeroIndex(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === heroIndex
                        ? 'w-5 bg-sky-400'
                        : 'w-1.5 bg-white/30 hover:bg-white/60'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={() => setHeroIndex((i) => Math.min(heroAlbums.length - 1, i + 1))}
                disabled={heroIndex === heroAlbums.length - 1}
                className="sm:hidden w-7 h-7 rounded-full bg-white/10 flex items-center justify-center disabled:opacity-30 transition-all shrink-0">
                <ChevronRight size={16} className="text-white" />
              </button>
            </div>
          </div>

          {/* 우측: 뮤직비디오 (PC only) */}
          {heroMusicVideos.length > 0 && (
            <MusicVideoSlider videos={heroMusicVideos} />
          )}
          </div>
          </div>
        </section>
      )}

      {/* ── 최신 앨범 그리드 ── */}
      {albums.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center justify-between mb-5 reveal">
            <h2 className="text-white text-xl font-bold">최신앨범</h2>
            <button
              onClick={() => navigate(`${PUBLIC_BASE}/albums`)}
              className="flex items-center gap-1 text-slate-400 hover:text-sky-400 text-sm transition-colors">
              전체 보기 <ChevronRight size={15} />
            </button>
          </div>
          <div className="stagger scroll-dark grid grid-rows-2 auto-cols-[32vw] grid-flow-col overflow-x-auto snap-x gap-2 pb-2 sm:grid-rows-1 sm:auto-cols-fr sm:grid-flow-row sm:overflow-x-visible sm:snap-none sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 sm:gap-4 sm:pb-0">
            {albums.map((album) => (
              <AlbumCard key={album.id} album={album} />
            ))}
          </div>
        </section>
      )}

      {/* ── 플레이리스트 ── */}
      {featuredPlaylists.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-6 border-t border-white/5">
          <div className="flex items-center justify-between mb-5 reveal">
            <h2 className="text-white text-xl font-bold">플레이리스트</h2>
            <button
              onClick={() => navigate(`${PUBLIC_BASE}/playlists`)}
              className="flex items-center gap-1 text-slate-400 hover:text-sky-400 text-sm transition-colors">
              전체 보기 <ChevronRight size={15} />
            </button>
          </div>
          <div className="stagger scroll-dark grid grid-rows-1 auto-cols-[55vw] grid-flow-col overflow-x-auto snap-x gap-3 pb-2 sm:auto-cols-fr sm:overflow-x-visible sm:snap-none sm:grid-cols-3 sm:pb-0">
            {featuredPlaylists.map((pl) => (
              <PlaylistMiniCard key={pl.id} playlist={pl} isNew={pl.weekOf === currentMonday} />
            ))}
          </div>
        </section>
      )}

      {/* ── 뮤직트렌드 ── */}
      {musicTrends.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-8 border-t border-white/5">
          <div className="flex items-center justify-between mb-5 reveal">
            <h2 className="text-white text-xl font-bold">뮤직트렌드</h2>
            <button
              onClick={() => navigate(`${PUBLIC_BASE}/magazine/music-trends`)}
              className="flex items-center gap-1 text-slate-400 hover:text-sky-400 text-sm transition-colors">
              전체 보기 <ChevronRight size={15} />
            </button>
          </div>
          <div className="stagger scroll-dark grid grid-rows-1 auto-cols-[55vw] grid-flow-col overflow-x-auto snap-x gap-3 pb-2 sm:grid-rows-none sm:auto-cols-auto sm:grid-flow-row sm:overflow-x-visible sm:snap-none sm:grid-cols-2 sm:pb-0">
            {musicTrends.map((trend, idx) => (
              <TrendMiniCard key={trend.id} trend={trend} isNew={trend.weekOf === currentMonday} hideOnPc={idx >= 2} />
            ))}
          </div>
        </section>
      )}
    </PublicLayout>
  );
}

export default PublicHomePage;
